import {
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import PageHeader, { HeaderTag }
  from '../../pricingJourney/components/PageHeader';

import MarginCenterTemporalContextBar
  from './MarginCenterTemporalContextBar';

import type {
  MarginCenterComparisonMode,
  MarginCenterTimeGrain,
} from '../types/marginCenter';


interface TemporalSelection {
  start_date: string | null;
  end_date: string | null;

  comparison_mode: MarginCenterComparisonMode;
  comparison_start_date: string | null;
  comparison_end_date: string | null;

  time_grain: MarginCenterTimeGrain;
}

interface TemporalDataset {
  first_transaction_date: string | null;
  last_transaction_date: string | null;
}


// PRC-032: cabecera común; el nombre es el título y sus datos clave van en la
// misma línea como etiquetas pequeñas; el periodo, en la barra fija de filtros.
export function MarginCenterEntityContextHeader({
  entityType,
  label,
  entityId,
  attributes,
  selection,
  dataset,
  onBack,
  backLabel = 'Volver al Explorer',
}: {
  entityType: 'Cliente' | 'Producto';
  label: string;
  entityId: string;
  attributes: string[];
  selection: TemporalSelection;
  dataset: TemporalDataset;
  onBack: () => void;
  backLabel?: string;
}) {
  return (
    <PageHeader
      eyebrow={`Explorer · ${entityType}`}
      title={label}
      back={{ label: backLabel, onClick: onBack }}
      meta={(
        <>
          <HeaderTag>ID {entityId}</HeaderTag>
          {attributes.map((attribute, index) => (
            <HeaderTag key={`${attribute}-${index}`}>{attribute}</HeaderTag>
          ))}
        </>
      )}
      filters={(
        <MarginCenterTemporalContextBar
          selection={selection}
          dataset={dataset}
          compact
        />
      )}
      sx={{ mb: 1 }}
    />
  );
}


export function MarginCenterContextMetricCard({
  label,
  value,
  change,
  changeValue,
  comparison,
  onClick,
}: {
  label: string;
  value: string;
  change?: string;
  changeValue?: number | null;
  comparison?: string;
  onClick?: () => void;
}) {
  const changeColor =
    changeValue === null
    || changeValue === undefined
    || changeValue === 0
      ? '#94a3b8'
      : changeValue > 0
        ? '#34d399'
        : '#fb7185';

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (
          onClick
          && (
            event.key === 'Enter'
            || event.key === ' '
          )
        ) {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        px: 2,
        py: 1.6,
        minHeight: 112,
        borderRadius: 3,
        border:
          '1px solid rgba(148,163,184,0.16)',
        bgcolor:
          'rgba(15,23,42,0.54)',
        ...(onClick
          ? {
              cursor: 'pointer',
              transition:
                'border-color 140ms ease, background-color 140ms ease, transform 140ms ease',

              '&:hover': {
                borderColor:
                  'rgba(56,189,248,0.42)',
                bgcolor:
                  'rgba(15,23,42,0.78)',
                transform:
                  'translateY(-1px)',
              },

              '&:focus-visible': {
                outline:
                  '2px solid rgba(56,189,248,0.70)',
                outlineOffset: 2,
              },
            }
          : {}),
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontWeight: 750,
          }}
        >
          {label}
        </Typography>

        {onClick && (
          <Typography
            variant="caption"
            sx={{
              color: '#38bdf8',
              fontWeight: 750,
            }}
          >
            Ver detalle
          </Typography>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="baseline"
        sx={{
          mt: 0.35,
          minWidth: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#f8fafc',
            fontWeight: 850,
            lineHeight: 1.25,
            flexShrink: 0,
          }}
        >
          {value}
        </Typography>

        {comparison && (
          <>
            <Typography
              variant="caption"
              sx={{
                color: '#475569',
                fontWeight: 650,
              }}
            >
              /
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontWeight: 650,
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
              }}
            >
              {comparison}
            </Typography>
          </>
        )}
      </Stack>

      {change && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.35,
            display: 'block',
            color: changeColor,
            fontWeight: 800,
          }}
        >
          {change}
        </Typography>
      )}
    </Paper>
  );
}